import { ft } from '../core/units.js';
import { whom } from '../core/words.js';
import { NEAR, TALKING } from './prompts.js';
import { rungFor } from './standing.js';

/** Feet, as far as a conversation reaches, and how far you walk before it ends. */
export const TALK_REACH = ft(7);
export const TALK_BREAK = ft(11);

/**
 * Who you are talking to, and the box that goes with it.
 *
 * It owns the two things a conversation is — who is in front of you and what
 * they are in the middle of saying — and it is the only thing that sets them.
 * Anybody else who needs somebody to start talking to you, like a manager
 * arriving with a warning, asks it to (`tell`), rather than reaching in.
 *
 * `dealFor` and `offerDeal` are the deal on the table, from `deals.js`.
 */
/*
 * `speaks` is anything somebody has to say to you that is not a greeting —
 * what they saw, while you are asking around — and wins over the greeting when
 * it has something. `met` is told who you walked up to. Neither knows what it
 * is for, and both are nothing until something hands them in.
 */
export function createConversation({
  npcs,
  player,
  rig,
  dialogue,
  regard,
  standing,
  dealFor,
  offerDeal,
  speaks = () => null,
  met = () => {},
}) {
  /**
   * Who you are close enough to talk to.
   *
   * NPCs move, so they cannot go through the prop-based interaction system, which
   * resolves its targets once at boot. Same test though — near enough, and facing
   * roughly the right way — because the two have to feel identical to the player.
   */
  let talkingTo = null;
  /**
   * Something one person is in the middle of saying to you, which is not a deal.
   *
   * Held rather than printed once, because `runTalking` redraws the box every
   * frame from whatever the person is currently offering — and a line that is
   * repainted out of existence on the next frame was never said.
   */
  let saying = null;

  function nearestNpc() {
    let best = null;
    for (const npc of npcs) {
      if (!npc.present) continue;
      const dx = npc.position.x - player.position.x;
      const dz = npc.position.z - player.position.z;
      const distance = Math.hypot(dx, dz);
      if (distance > TALK_REACH) continue;
      const facing = distance < 0.3 ? 1 : (dx / distance) * rig.forward.x + (dz / distance) * rig.forward.z;
      if (facing < 0.35) continue;
      if (!best || distance < best.distance) best = { npc, distance };
    }
    return best;
  }

  /** Ends any conversation, because something with a higher claim is in reach. */
  function clearTalking() {
    saying = null;
    if (!talkingTo) {
      dialogue.close();
      return null;
    }
    talkingTo = null;
    dialogue.close();
    return null;
  }

  /**
   * What this person says to you, off the ladder they are read on.
   *
   * Two ledgers, one lookup. A colleague is a person, so what they say comes off
   * what *they* think of you. A gate is the face of a department, so what they
   * say comes off what the department thinks, which is the same four steps and
   * the same four words the rest of the game is written in.
   *
   * That is the whole of the wiring this run needed: the table was already keyed
   * by rung and both ledgers were already keyed by rung. Nothing here decides
   * anything, moves anything, or is read by anything but the box.
   */
  function greetingFor(npc) {
    const said = speaks(npc);
    if (said) return said;
    const id = npc.data.id;
    const greets = npc.data.greets;
    if (!greets) return null;
    const where = regard.ids.includes(id)
      ? regard.get(id)
      : standing.get(npc.data.department);
    return greets[rungFor(where).id] ?? null;
  }

  /**
   * Talking to somebody: what the key offers, and the box that goes with it.
   *
   * Drawing the box is a side effect of somebody being in front of you and has to
   * happen every frame; what the key *does* about them is the candidates. The two
   * used to be the same function and it took the press as a side effect of being
   * asked whether there was anything to say.
   */
  function runDialogue() {
    if (talkingTo) {
      const dx = talkingTo.position.x - player.position.x;
      const dz = talkingTo.position.z - player.position.z;
      // Walking away ends it. Nobody follows you down the corridor mid-sentence.
      if (Math.hypot(dx, dz) > TALK_BREAK) {
        clearTalking();
        return null;
      }
      const deal = dealFor(talkingTo);
      dialogue.show(
        talkingTo,
        deal,
        saying?.npc === talkingTo ? saying.notice : null,
        greetingFor(talkingTo)
      );
      return deal;
    }
    dialogue.close();
    return null;
  }

  function offerTalk(deal) {
    const out = [];
    if (talkingTo) {
      out.push(...offerDeal(deal));
      out.push({
        id: 'leave',
        tier: TALKING,
        distance: 90,
        verb: 'leave',
        what: whom(talkingTo.data.name),
        run: () => clearTalking(),
      });
      return out;
    }
    const near = nearestNpc();
    if (!near) return out;
    out.push({
      id: 'talk',
      tier: NEAR,
      distance: near.distance,
      // Where they are, for aiming and for the prompt drawn over their head.
      at: near.npc.position,
      focus: `npc:${near.npc.data.id}`,
      height: 2.05,
      verb: 'talk to',
      what: whom(near.npc.data.name),
      run() {
        talkingTo = near.npc;
        met(talkingTo);
        dialogue.show(talkingTo, dealFor(talkingTo), null, greetingFor(talkingTo));
      },
    });
    return out;
  }

  return {
    nearestNpc,
    clearTalking,
    greetingFor,
    runDialogue,
    offerTalk,
    get talkingTo() {
      return talkingTo;
    },
    /** Somebody has come to you with something to say, and says it. */
    tell(npc, notice) {
      talkingTo = npc;
      saying = { npc, notice };
      dialogue.show(talkingTo, null, saying.notice);
    },
    /** A new week: nobody is talking to you. */
    forget() {
      talkingTo = null;
    },
  };
}
